import { useEffect, useRef } from 'react'

import { useFlow, UseFlowResponse } from '@cash-web/shared-flow-feature-main'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { JourneyData } from '@cash-web/shared-journey-util-main'

import { JourneyStore } from './JourneyStoreProvider'

export const JourneyFlowInstance = ({
  journeyData,
  store,
  onBlockerView,
  onCompleteFlow,
  onCancelFlow,
  ...useFlowParams
}: {
  journeyData: JourneyData
  store?: JourneyStore
  onBlockerView?: (flow: UseFlowResponse) => void
  onCompleteFlow?: (flow: UseFlowResponse) => void
  onCancelFlow?: (flow: UseFlowResponse) => void
} & Omit<Parameters<typeof useFlow>[0], 'onBlockerView' | 'onFlowComplete' | 'onFlowCancel'>) => {
  const journey = useJourneyContext()
  // isUnmounting is used to prevent calling onCompleteFlow if the journey is being unmounted
  const isUnmounting = useRef(false)
  // isFlowCompleted is used to prevent calling flow.complete if the flow has already been completed
  const isFlowCompleted = useRef(false)

  const flow = useFlow({
    ...useFlowParams,
    onRevalidateRoute: journey.revalidateBrowserRoute,
    onBlockerView: () => onBlockerView?.(flow),
    onCompleteFlow: () => {
      /**
       * Skip journey.end() if component is unmounting (already removed from history)
       * This prevents navigation issues by ensuring we don't navigate back before journey has started
       */
      if (!isUnmounting.current) {
        isFlowCompleted.current = true
        journey.complete(journeyData.journeyId)
      }
      onCompleteFlow?.(flow)
    },
    onCancelFlow: () => onCancelFlow?.(flow),
  })

  const prevResultRef = useRef<UseFlowResponse>()

  // updates the store with the flow
  useEffect(() => {
    if (prevResultRef.current !== flow) {
      store?.updateProperty(journeyData.journeyId, 'flow', flow)
      prevResultRef.current = flow
    }
  }, [flow, store, journeyData.journeyId])

  useEffect(() => {
    isUnmounting.current = false
    return () => {
      isUnmounting.current = true
      // if the journey is being unmounted, we need to complete the flow
      if (flow.isInProgress && !isFlowCompleted.current) {
        // if we are unmounting we are not able to display any dismiss blockers
        flow.cancel({ force: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.isInProgress])

  return null
}
